import React, { useState, useEffect } from "react";
import {
  Users,
  Search,
  MoreHorizontal,
  Mail,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Filter,
  Trash2,
  UserX,
  UserCheck,
  BarChart3,
  ArrowRight,
  X as CloseIcon,
  Video,
  Download,
  Clock,
  ShieldCheck,
  ShieldX,
  UserCog,
} from "lucide-react";
import { adminService } from "../../services/admin.service";
import { apiClient } from "../../lib/api-client";
import {
  Sidebar,
  Header,
  ToastContainer,
} from "../Dashboard/DashboardComponents";
import { authService } from "../../services/auth.service";
import { useNavigate } from "react-router-dom";
import { User, Project, ExportRequest } from "../../types/api.types";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

// Helper: get the real user ID
const getUserId = (user: User): string => (user._id || user.id) as string;

// --- Delete Confirmation Modal ---
const DeleteConfirmModal = ({
  user,
  onConfirm,
  onCancel,
}: {
  user: User;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
    >
      <div className="p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-6">
          <Trash2 size={28} className="text-rose-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">Delete User?</h3>
        <p className="text-slate-500 font-medium text-sm leading-relaxed mb-6">
          Are you sure you want to delete{" "}
          <strong className="text-slate-900">{user.name}</strong>? This will
          permanently remove all their data.
        </p>

        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-left mb-8">
          <p className="text-xs font-bold text-amber-700 flex items-center gap-2 mb-2">
            <AlertCircle size={14} /> Important Notice
          </p>
          <ul className="text-[11px] text-amber-700/80 space-y-1 font-medium list-disc pl-4">
            <li>User account will be removed</li>
            <li>All video projects will be deleted</li>
            <li>Billing history will be archived</li>
          </ul>
        </div>

        <div className="flex gap-3">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="flex-1 h-11 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 font-bold"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="flex-1 h-11 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold transition-all shadow-sm"
          >
            Delete
          </Button>
        </div>
      </div>
    </motion.div>
  </div>
);

// --- Activity Modal Component ---
const ActivityModal = ({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) => {
  const [activity, setActivity] = useState<{
    projects: Project[];
    exports: ExportRequest[];
    stats: {
      projectCount: number;
      exportCount: number;
      totalEstimatedCost: number;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadActivity = async () => {
      try {
        const data = await adminService.getUserActivity(getUserId(user));
        setActivity(data);
      } catch (err) {
        console.error("Failed to load user activity:", err);
      } finally {
        setLoading(false);
      }
    };
    loadActivity();
  }, [user.id, user._id]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.98, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-[32px] w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
      >
        {/* Modal Header */}
        <div className="p-6 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-bold text-lg shadow-md shadow-indigo-100">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{user.name}</h3>
              <p className="text-xs text-slate-500 font-medium">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-slate-50 rounded-xl transition-colors text-slate-400"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-10 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              <span className="text-sm text-slate-500 font-medium">
                Loading activity...
              </span>
            </div>
          ) : activity ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100 group">
                  <div className="flex items-center gap-2 mb-4">
                    <Video className="w-4 h-4 text-slate-400" />
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Projects
                    </span>
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 tabular-nums">
                    {activity.stats.projectCount}
                  </h4>
                </div>
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-2 mb-4">
                    <Download className="w-4 h-4 text-slate-400" />
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Exports
                    </span>
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 tabular-nums">
                    {activity.stats.exportCount}
                  </h4>
                </div>
                <div className="p-6 rounded-2xl bg-indigo-50 border border-indigo-100">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="w-4 h-4 text-indigo-400" />
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Est. Cost
                    </span>
                  </div>
                  <h4 className="text-2xl font-bold text-indigo-700 tabular-nums">
                    ${activity.stats.totalEstimatedCost.toFixed(3)}
                  </h4>
                </div>
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="w-4 h-4 text-slate-400" />
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Storage Used
                    </span>
                  </div>
                  <div className="flex items-end gap-2">
                    <h4 className="text-2xl font-bold text-slate-900 tabular-nums">
                      {((user.storageUsed || 0) / (1024 * 1024 * 1024)).toFixed(
                        2,
                      )}{" "}
                      GB
                    </h4>
                    <span className="text-xs text-slate-400 font-medium mb-1">
                      / 30 GB
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5 mt-4">
                    <div
                      className={`h-1.5 rounded-full ${(user.storageUsed || 0) / (30 * 1024 * 1024 * 1024) > 0.9 ? "bg-rose-500" : "bg-indigo-600"}`}
                      style={{
                        width: `${Math.min(100, ((user.storageUsed || 0) / (30 * 1024 * 1024 * 1024)) * 100)}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Projects List */}
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-600"></div>
                    Recent Projects
                  </h4>
                  <div className="space-y-2">
                    {(activity.projects || []).length > 0 ? (
                      (activity.projects || []).slice(0, 5).map((project) => (
                        <div
                          key={project._id || project.id}
                          className="p-4 rounded-xl border border-slate-100 flex items-center justify-between hover:bg-slate-50 transition-all group"
                        >
                          <div className="min-w-0">
                            <p className="font-bold text-slate-900 text-sm truncate">
                              {project.title}
                            </p>
                            <p className="text-[10px] text-slate-400 font-medium">
                              Modified:{" "}
                              {new Date(project.updatedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <ArrowRight
                            size={14}
                            className="text-slate-300 group-hover:text-indigo-600 transition-colors"
                          />
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center rounded-2xl bg-slate-50 border border-dashed border-slate-200">
                        <p className="text-xs text-slate-400 font-medium italic">
                          No projects found
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Exports List */}
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                    Export History
                  </h4>
                  <div className="space-y-2">
                    {(activity.exports || []).length > 0 ? (
                      (activity.exports || []).slice(0, 5).map((exp) => (
                        <div
                          key={exp._id || exp.id}
                          className="p-4 rounded-xl border border-slate-100 flex items-center justify-between hover:bg-slate-50 transition-all"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <div
                                className={`w-1.5 h-1.5 rounded-full ${exp.status === "completed" ? "bg-emerald-500" : "bg-rose-500"}`}
                              />
                              <p className="font-bold text-slate-900 text-sm truncate">
                                Export #{(exp._id || exp.id || "").slice(-6)}
                              </p>
                            </div>
                            <p className="text-[10px] text-slate-400 font-medium">
                              {new Date(exp.createdAt).toLocaleDateString()} • $
                              {exp.estimatedCost?.toFixed(3) || "0.000"}
                            </p>
                          </div>
                          {exp.outputUrl && (
                            <a
                              href={exp.outputUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="w-8 h-8 flex items-center justify-center bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition-all"
                            >
                              <Download size={14} />
                            </a>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center rounded-2xl bg-slate-50 border border-dashed border-slate-200">
                        <p className="text-xs text-slate-400 font-medium italic">
                          No exports found
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="py-20 text-center">
              <AlertCircle className="mx-auto h-10 w-10 text-rose-500/50 mb-4" />
              <p className="text-sm text-rose-600 font-semibold">
                Could not load activity data.
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-slate-100 flex justify-end">
          <Button
            onClick={onClose}
            variant="ghost"
            className="h-11 px-8 rounded-xl text-slate-700 font-bold hover:bg-slate-50"
          >
            Close
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// --- Extend Trial Modal ---
const ExtendTrialModal = ({
  user,
  onConfirm,
  onCancel,
}: {
  user: User;
  onConfirm: (date: string) => void;
  onCancel: () => void;
}) => {
  const [date, setDate] = useState(
    user.trialEndsAt
      ? new Date(user.trialEndsAt).toISOString().split("T")[0]
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onConfirm(date);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
      >
        <form onSubmit={handleSubmit}>
          <div className="p-8">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-6">
              <Clock size={28} className="text-indigo-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">
              Extend Trial
            </h3>
            <p className="text-slate-500 font-medium text-sm leading-relaxed mb-6 text-center">
              Set a new trial expiration date for{" "}
              <strong className="text-slate-900">{user.name}</strong>.
            </p>

            <div className="space-y-4 mb-8">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Expiration Date
                </label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  className="h-11 rounded-xl border-slate-200 focus:ring-indigo-500/10 focus:border-indigo-500 font-medium"
                />
                <p className="text-[10px] text-slate-400 font-medium italic">
                  Trial currently ends:{" "}
                  {user.trialEndsAt
                    ? new Date(user.trialEndsAt).toLocaleDateString()
                    : "Not set"}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                className="flex-1 h-11 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 font-bold"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 h-11 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-sm"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Extend Trial"
                )}
              </Button>
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// --- Role Confirmation Modal ---
const RoleConfirmModal = ({
  user,
  newRole,
  onConfirm,
  onCancel,
}: {
  user: User;
  newRole: "user" | "admin";
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
    >
      <div className="p-8 text-center">
        <div
          className={`w-16 h-16 rounded-2xl ${newRole === "admin" ? "bg-indigo-50" : "bg-amber-50"} flex items-center justify-center mx-auto mb-6`}
        >
          {newRole === "admin" ? (
            <ShieldCheck size={28} className="text-indigo-600" />
          ) : (
            <ShieldX size={28} className="text-amber-600" />
          )}
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">
          {newRole === "admin"
            ? "Promote to Admin?"
            : "Remove Admin Privileges?"}
        </h3>
        <p className="text-slate-500 font-medium text-sm leading-relaxed mb-6">
          {newRole === "admin" ? (
            <>
              Are you sure you want to make{" "}
              <strong className="text-slate-900">{user.name}</strong> an
              administrator? They will have full access to all settings and user
              data.
            </>
          ) : (
            <>
              Are you sure you want to remove admin privileges from{" "}
              <strong className="text-slate-900">{user.name}</strong>?
            </>
          )}
        </p>

        <div className="flex gap-3">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="flex-1 h-11 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 font-bold"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className={`flex-1 h-11 ${newRole === "admin" ? "bg-indigo-600 hover:bg-indigo-700" : "bg-amber-600 hover:bg-amber-700"} text-white rounded-xl font-bold transition-all shadow-sm`}
          >
            {newRole === "admin" ? "Promote" : "Demote"}
          </Button>
        </div>
      </div>
    </motion.div>
  </div>
);

// --- Main Component ---
const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMigrating, setIsMigrating] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToExtendTrial, setUserToExtendTrial] = useState<User | null>(null);
  const [userToUpdateRole, setUserToUpdateRole] = useState<User | null>(null);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const data = await adminService.getUsers();
      setUsers(data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const userData = await authService.getProfile();
        if (userData.role !== "admin") {
          navigate("/dashboard");
          return;
        }
        setCurrentUser(userData);
        await fetchUsers();
      } catch (err) {
        console.error("User management load error:", err);
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
      setSidebarOpen(window.innerWidth >= 1024);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleToggleStatus = async (user: User) => {
    try {
      await adminService.toggleUserStatus(getUserId(user));
      await fetchUsers();
    } catch (err) {
      console.error("Failed to toggle status:", err);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await adminService.deleteUser(getUserId(userToDelete));
      setUserToDelete(null);
      await fetchUsers();
    } catch (err) {
      console.error("Failed to delete user:", err);
    }
  };

  const handleExtendTrial = async (newDate: string) => {
    if (!userToExtendTrial) return;
    try {
      await adminService.extendTrial(getUserId(userToExtendTrial), newDate);
      setUserToExtendTrial(null);
      await fetchUsers();
    } catch (err) {
      console.error("Failed to extend trial:", err);
      throw err;
    }
  };

  const handleUpdateRole = async () => {
    if (!userToUpdateRole) return;
    try {
      const newRole = userToUpdateRole.role === "admin" ? "user" : "admin";
      await adminService.updateUserRole(getUserId(userToUpdateRole), newRole);
      setUserToUpdateRole(null);
      await fetchUsers();
    } catch (err) {
      console.error("Failed to update role:", err);
    }
  };

  const handleRunMigration = async () => {
    if (
      !window.confirm("This will recalculate storage for all users. Proceed?")
    )
      return;
    setIsMigrating(true);
    try {
      await apiClient.post("admin/system/migrate-storage");
      alert("Migration completed successfully");
      await fetchUsers();
    } catch (error: any) {
      alert("Migration failed: " + (error.message || "Unknown error"));
    } finally {
      setIsMigrating(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
          <span className="text-sm text-slate-500 font-medium">
            Loading users...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 font-sans">
      <Sidebar
        isOpen={sidebarOpen}
        isMobile={isMobile}
        user={currentUser}
        onToggle={toggleSidebar}
        activePage="admin-users"
      />

      <div className="flex-1 flex flex-col overflow-hidden relative z-10">
        <Header
          isMobile={isMobile}
          onToggleSidebar={toggleSidebar}
          user={currentUser}
          onLogout={() => {
            authService.logout();
            navigate("/login");
          }}
          title="User Directory"
        />

        <main className="flex-1 overflow-y-auto p-6 lg:p-10 custom-scrollbar">
          <div className="max-w-7xl mx-auto space-y-10">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                  User Management
                </h1>
                <p className="text-slate-500 text-sm mt-1">
                  Review activity and manage access.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleRunMigration}
                  disabled={isMigrating}
                  variant="outline"
                  className="h-11 px-4 rounded-xl border-slate-200 bg-white text-slate-600 font-semibold hover:bg-slate-50 disabled:opacity-50"
                >
                  <BarChart3
                    className={`w-4 h-4 mr-2 ${isMigrating ? "animate-pulse" : ""}`}
                  />
                  {isMigrating ? "Syncing..." : "Sync Storage"}
                </Button>
                <div className="relative group flex-1 sm:flex-none">
                  <Search
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors"
                    size={16}
                  />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full lg:w-72 h-11 bg-white border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/10 focus:border-indigo-500 text-sm font-medium transition-all"
                  />
                </div>
                <Button
                  variant="outline"
                  className="h-11 px-4 rounded-xl border-slate-200 bg-white text-slate-600 font-semibold hover:bg-slate-50"
                >
                  <Filter size={16} className="mr-2" />
                  Filter
                </Button>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Member
                      </th>
                      <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Plan
                      </th>
                      <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Source
                      </th>
                      <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Storage
                      </th>
                      <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Status
                      </th>
                      <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Joined
                      </th>
                      <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Last Logged In
                      </th>
                      <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500 text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.map((user) => {
                      const isTrialExpired =
                        user.subscriptionStatus === "trial" &&
                        user.trialEndsAt &&
                        new Date(user.trialEndsAt) < new Date();
                      const displayStatus = isTrialExpired
                        ? "expired"
                        : user.subscriptionStatus || "free";

                      return (
                        <tr
                          key={user.id}
                          className="hover:bg-slate-50/50 transition-colors group"
                        >
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-100 text-slate-600 flex items-center justify-center font-bold text-sm">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-bold text-slate-900 text-sm truncate">
                                    {user.name}
                                  </p>
                                  {user.role === "admin" && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[8px] font-black uppercase tracking-tighter">
                                      <ShieldCheck size={8} /> Admin
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-400 font-medium truncate">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                                displayStatus === "active"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : displayStatus === "trial"
                                    ? "bg-indigo-50 text-indigo-700"
                                    : displayStatus === "expired"
                                      ? "bg-rose-50 text-rose-700"
                                      : "bg-slate-100 text-slate-500"
                              }`}
                            >
                              {displayStatus === "active" ? (
                                <CheckCircle2 size={10} />
                              ) : (
                                <AlertCircle size={10} />
                              )}
                              {displayStatus}
                            </span>
                          </td>
                          <td className="px-6 py-5">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                              {(user as any).referralSource || "Direct"}
                            </span>
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex flex-col gap-1.5">
                              <div className="flex justify-between items-center text-[10px] font-bold">
                                <span className="text-slate-600">
                                  {(
                                    (user.storageUsed || 0) /
                                    (1024 * 1024 * 1024)
                                  ).toFixed(1)}
                                  GB
                                </span>
                                <span className="text-slate-400">30GB</span>
                              </div>
                              <div className="w-20 bg-slate-100 rounded-full h-1 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${(user.storageUsed || 0) / (30 * 1024 * 1024 * 1024) > 0.9 ? "bg-rose-500" : "bg-indigo-600"}`}
                                  style={{
                                    width: `${Math.min(100, ((user.storageUsed || 0) / (30 * 1024 * 1024 * 1024)) * 100)}%`,
                                  }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-1.5 h-1.5 rounded-full ${user.status === "suspended" ? "bg-rose-500" : "bg-emerald-500"}`}
                              />
                              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">
                                {user.status || "ACTIVE"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                              <Calendar size={14} className="text-slate-300" />
                              {new Date(user.createdAt).toLocaleDateString(
                                undefined,
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                              {user.lastLoginAt ? (
                                <>
                                  <Clock size={14} className="text-slate-300" />
                                  {new Date(user.lastLoginAt).toLocaleString(
                                    undefined,
                                    {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                      hour: "numeric",
                                      minute: "numeric",
                                    },
                                  )}
                                </>
                              ) : (
                                <span className="text-slate-400 italic">
                                  Never
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-5 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-900">
                                  <MoreHorizontal size={18} />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                className="w-56 bg-white border border-slate-200 text-slate-900 rounded-2xl p-2 shadow-2xl"
                              >
                                <DropdownMenuLabel className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                  Account Actions
                                </DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => setSelectedUser(user)}
                                  className="flex items-center gap-3 px-3 py-2 rounded-xl focus:bg-slate-50 focus:text-indigo-600 cursor-pointer"
                                >
                                  <BarChart3 size={16} />
                                  <span className="text-sm font-semibold">
                                    View Activity
                                  </span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-slate-100 mx-2" />
                                <DropdownMenuItem
                                  onClick={() => handleToggleStatus(user)}
                                  className="flex items-center gap-3 px-3 py-2 rounded-xl focus:bg-slate-50 focus:text-slate-900 cursor-pointer"
                                >
                                  {user.status === "suspended" ? (
                                    <>
                                      <UserCheck
                                        size={16}
                                        className="text-emerald-600"
                                      />
                                      <span className="text-sm font-semibold">
                                        Unlock Account
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <UserX
                                        size={16}
                                        className="text-amber-600"
                                      />
                                      <span className="text-sm font-semibold">
                                        Lock Account
                                      </span>
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setUserToExtendTrial(user)}
                                  className="flex items-center gap-3 px-3 py-2 rounded-xl focus:bg-slate-50 focus:text-indigo-600 cursor-pointer"
                                >
                                  <Clock size={16} />
                                  <span className="text-sm font-semibold">
                                    Extend Trial
                                  </span>
                                </DropdownMenuItem>
                                {getUserId(user) !== currentUser?.id && (
                                  <DropdownMenuItem
                                    onClick={() => setUserToUpdateRole(user)}
                                    className={`flex items-center gap-3 px-3 py-2 rounded-xl focus:bg-slate-50 cursor-pointer ${user.role === "admin" ? "focus:text-amber-600" : "focus:text-indigo-600"}`}
                                  >
                                    {user.role === "admin" ? (
                                      <>
                                        <ShieldX
                                          size={16}
                                          className="text-amber-600"
                                        />
                                        <span className="text-sm font-semibold">
                                          Remove Admin
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <ShieldCheck
                                          size={16}
                                          className="text-indigo-600"
                                        />
                                        <span className="text-sm font-semibold">
                                          Make Admin
                                        </span>
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => setUserToDelete(user)}
                                  className="flex items-center gap-3 px-3 py-2 rounded-xl focus:bg-rose-50 focus:text-rose-600 cursor-pointer text-rose-600"
                                >
                                  <Trash2 size={16} />
                                  <span className="text-sm font-semibold">
                                    Delete User
                                  </span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length === 0 && (
                <div className="py-24 text-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-slate-300" />
                  </div>
                  <h3 className="text-base font-bold text-slate-400 uppercase tracking-tight">
                    No results matched
                  </h3>
                  <p className="text-xs text-slate-400 font-medium mt-1">
                    Try refining your search terms.
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {selectedUser && (
          <ActivityModal
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {userToDelete && (
          <DeleteConfirmModal
            user={userToDelete}
            onConfirm={handleDeleteUser}
            onCancel={() => setUserToDelete(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {userToExtendTrial && (
          <ExtendTrialModal
            user={userToExtendTrial}
            onConfirm={handleExtendTrial}
            onCancel={() => setUserToExtendTrial(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {userToUpdateRole && (
          <RoleConfirmModal
            user={userToUpdateRole}
            newRole={userToUpdateRole.role === "admin" ? "user" : "admin"}
            onConfirm={handleUpdateRole}
            onCancel={() => setUserToUpdateRole(null)}
          />
        )}
      </AnimatePresence>

      <ToastContainer />
    </div>
  );
};

export default UserManagement;
